import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import classNames from 'classnames';

import BaseModal from '../Base/Modal/BaseModal';
import UploadCandidatePictureAndTutorial from '../UploadCandidatePictureAndTutorial';
import { CANDIDATE_PICTURE } from '../../graphql/queries';

import './styles.scss';

const CandidatePictureUpload = ({
  candidate,
  sessionId,
  withInstructions = true,
  displayOnly = false,
  onFinished = undefined,
  required = false,
}) => {
  const apolloClient = useApolloClient();
  const [isModalOpened, setIsModalOpened] = useState(false);
  const { t } = useTranslation();
  const [pictureUrl, setPictureUrl] = useState(candidate.pictureUrl);

  const onUploadFinished = async ({ candidatePictureResult }) => {
    candidate.pictureId = candidatePictureResult.id;

    const observer = apolloClient.watchQuery({
      query: CANDIDATE_PICTURE,
      variables: { uniqId: candidatePictureResult.uniqId },
      fetchPolicy: 'no-cache',
      pollInterval: 2000,
    });

    observer.subscribe({
      next: ({ data }) => {
        if (data.candidatePicture.url) {
          observer.stopPolling();

          setPictureUrl(data.candidatePicture.url);

          if (onFinished) {
            onFinished(candidate);
          }
        }
      },
    });
  };

  return (
    <>
      <div className={withInstructions ? 'candidate-infos__photo' : 'account-session-candidate__photo'}>
        {displayOnly && (!candidate || !pictureUrl) && (
          <div className="candidate-infos__photo candidate-infos__photo--no-photo" />
        )}
        {displayOnly && candidate && pictureUrl && (
          <img alt={`${candidate.firstname} ${candidate.lastname}`} src={pictureUrl} />
        )}
        {!displayOnly && (
          <>
            <button
              className={'account-session-candidate__photo-button'}
              onClick={() => setIsModalOpened(true)}
              type={'button'}
              aria-label="candidate photo button"
            >
              {(!candidate || !pictureUrl) && (
                <div className="candidate-infos__photo candidate-infos__photo--no-photo" />
              )}
              {candidate && pictureUrl && <img alt={`${candidate.firstname} ${candidate.lastname}`} src={pictureUrl} />}
            </button>
            <BaseModal isOpen={isModalOpened} onClose={() => setIsModalOpened(false)}>
              <UploadCandidatePictureAndTutorial
                sessionId={sessionId}
                token={candidate.token}
                onUploadFinished={onUploadFinished}
              />
            </BaseModal>
          </>
        )}
      </div>
      {withInstructions && (
        <div
          className={classNames('candidate-infos__upload-instructions', {
            'candidate-infos__upload-instructions--required': required,
          })}
          dangerouslySetInnerHTML={{ __html: t('session-registration.candidate.picture-upload.instructions') }}
        />
      )}
    </>
  );
};

CandidatePictureUpload.propTypes = {
  candidate: PropTypes.shape({
    token: PropTypes.string,
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    email: PropTypes.string,
    pictureId: PropTypes.number,
    pictureUrl: PropTypes.string,
  }).isRequired,
  sessionId: PropTypes.number.isRequired,
  withInstructions: PropTypes.bool,
  displayOnly: PropTypes.bool,
  onFinished: PropTypes.func,
  required: PropTypes.bool,
};

export default CandidatePictureUpload;
